/**
 * Legal pages (/terms, /privacy) inside the SPA.
 *
 * The register screen's "I agree to the Terms & Privacy Policy" links point at
 * /terms and /privacy. Before this screen existed the router's default branch
 * bounced both to /login, so the only thing a user could do with the consent
 * links was lose their half-filled form — a real compliance problem for a game
 * that takes payments. Content comes from the CMS (`GET /api/legal/:slug`), so
 * the operator edits it in the admin panel and no deploy is needed.
 *
 * The markup deliberately reuses the auth screens' backdrop (`bg-auth-gradient`)
 * and the parchment card, because these pages are almost always reached FROM
 * the register board in a new tab — a plain white document would read as a
 * different product.
 */
import { api } from '../network/api';

interface LegalPage {
  slug: string;
  title: string;
  content: string;
  updatedAt?: string;
  /** What the operator set in the panel; falls back to updatedAt server-side. */
  effectiveDate?: string;
}

/**
 * "Last updated 4 March 2026" — the one fact a reader checks before trusting a
 * legal document, and until now the screen fetched the date and threw it away.
 * A day-precision format with no time of day: the effective date of Terms is a
 * date, not a moment.
 */
export function legalDateLine(page: Pick<LegalPage, 'effectiveDate' | 'updatedAt'>): string {
  const raw = page.effectiveDate ?? page.updatedAt;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return `Last updated ${d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  })}`;
}

/**
 * Renders CMS text as HTML. The body is escaped FIRST and only a fixed set of
 * tags is reintroduced afterwards, so even though the author is an admin a
 * pasted `<script>` can never execute. Supports the small subset admins
 * actually type into the panel's textarea: blank-line paragraphs, `#`/`##`
 * headings, `-` bullets, `**bold**` and `[text](url)` links.
 */
export function renderLegalMarkdown(src: string): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const inline = (s: string) =>
    esc(s)
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g, '<a href="$2">$1</a>');

  return (src ?? '')
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => {
      const lines = block.split('\n').filter((l) => l.trim() !== '');
      if (!lines.length) return '';
      if (lines.length === 1 && /^###\s+/.test(lines[0])) return `<h3>${inline(lines[0].replace(/^###\s+/, ''))}</h3>`;
      if (lines.length === 1 && /^##?\s+/.test(lines[0])) return `<h2>${inline(lines[0].replace(/^##?\s+/, ''))}</h2>`;
      if (lines.every((l) => /^\s*[-*]\s+/.test(l)))
        return `<ul>${lines.map((l) => `<li>${inline(l.replace(/^\s*[-*]\s+/, ''))}</li>`).join('')}</ul>`;
      return `<p>${lines.map(inline).join('<br />')}</p>`;
    })
    .join('');
}

export function renderLegal(root: HTMLElement, slug: string): () => void {
  root.innerHTML = `
    <div class="min-h-screen bg-auth-gradient px-4 py-8">
      <div class="legal-sheet">
        <a class="legal-back" href="/login">‹ Back</a>
        <h1 class="legal-title" id="legal-title">Loading…</h1>
        <p class="legal-updated" id="legal-updated"></p>
        <div class="legal-body" id="legal-body"></div>
      </div>
    </div>
  `;

  const titleEl = root.querySelector('#legal-title') as HTMLHeadingElement;
  const bodyEl = root.querySelector('#legal-body') as HTMLDivElement;
  const dateEl = root.querySelector('#legal-updated') as HTMLParagraphElement;

  let cancelled = false;
  api
    .get<LegalPage>(`/legal/${encodeURIComponent(slug)}`)
    .then((page) => {
      if (cancelled) return;
      titleEl.textContent = page.title;
      document.title = `${page.title} — Terraboma`;
      dateEl.textContent = legalDateLine(page);
      bodyEl.innerHTML = renderLegalMarkdown(page.content);
    })
    .catch(() => {
      if (cancelled) return;
      // A missing page must still be a readable page, not a blank screen —
      // the visitor arrived here from a consent checkbox and needs a way on.
      // Three slugs share this screen, so the fallback heading has to name the
      // right one — a visitor who clicked "Cookies" must not land on a page
      // titled "Terms of Service".
      titleEl.textContent =
        slug === 'privacy' ? 'Privacy Policy' : slug === 'cookies' ? 'Cookie Policy' : 'Terms of Service';
      bodyEl.innerHTML =
        '<p>This document has not been published yet. Please contact support before creating an account if you need it.</p>';
    });

  return () => { cancelled = true; };
}
